/*
 * Emissão de Notas e Faltas — migração de `EmitirNotasFaltas.aspx`, com os seis modelos do
 * legado numa tela só. Ver `docs/emissao-notas-faltas/README.md`.
 */

/**
 * Os seis modelos. Os números são os MESMOS do `?modo=` da URL legada — o back usa o mesmo
 * `ModeloNotasFaltas`, e manter o número igual é o que permite conferir uma emissão nova contra
 * a antiga sem tabela de tradução no meio.
 */
export enum eModeloNotasFaltas {
  /** Quem ainda não teve nota ou frequência lançada na etapa. */
  NotasFrequenciasPendentes = 1,

  /** O quadro de notas e faltas da etapa, disciplina a disciplina. */
  NotasFaltasDaEtapa = 2,

  /** O acumulado do ano até a etapa escolhida. */
  AproveitamentoAcumulado = 3,

  /** O mesmo dia de aula lançado em mais de uma disciplina. */
  FrequenciasConflitantes = 4,

  /** Disciplina sem nota num estudante que já tem nota nas outras. */
  NotasDivergentes = 5,

  /** Quantos estudantes ficaram abaixo da média, por disciplina e etapa. */
  AbaixoDaMedia = 6,
}

/** Título e explicação de cada modelo. É o que a tela mostra no topo. */
export const DESCRICAO_DO_MODELO: Record<
  number,
  { titulo: string; explicacao: string }
> = {
  [eModeloNotasFaltas.NotasFrequenciasPendentes]: {
    titulo: 'Notas e Frequências Pendentes',
    explicacao:
      'Aponta, por estudante e disciplina, o que ainda falta lançar na etapa: a nota, o total '
      + 'de faltas, ou os dois.',
  },
  [eModeloNotasFaltas.NotasFaltasDaEtapa]: {
    titulo: 'Notas e Faltas do Bimestre',
    explicacao:
      'O quadro completo da etapa: a nota e o total de faltas de cada estudante em cada '
      + 'disciplina.',
  },
  [eModeloNotasFaltas.AproveitamentoAcumulado]: {
    titulo: 'Aproveitamento Acumulado',
    explicacao:
      'O total do ano até a etapa escolhida, somando as etapas anteriores e considerando a '
      + 'recuperação paralela de cada uma.',
  },
  [eModeloNotasFaltas.FrequenciasConflitantes]: {
    titulo: 'Frequências Conflitantes',
    explicacao:
      'Dias em que o mesmo estudante tem aula lançada em mais de uma disciplina no mesmo '
      + 'horário — a falta do dia acaba contada duas vezes.',
  },
  [eModeloNotasFaltas.NotasDivergentes]: {
    titulo: 'Notas Divergentes',
    explicacao:
      'Disciplinas SEM nota num estudante que já tem nota lançada nas outras da mesma etapa. '
      + 'Diferente das pendências: aqui a etapa já rodou e esta ficou para trás.',
  },
  [eModeloNotasFaltas.AbaixoDaMedia]: {
    titulo: 'Abaixo da Média',
    explicacao:
      'Quantos estudantes ficaram abaixo do mínimo exigido, por disciplina e por etapa. '
      + 'Percorre o ano inteiro.',
  },
};

/**
 * A etapa como o combo `/Turma/RecuperarEtapasAproveitamentoTurmas` devolve, e como o relatório
 * a consome. `sigla` + `etapa` são o que o servidor traduz para a coluna da AVALIACAO.
 */
export interface EtapaNotasFaltasModel {
  id: number;
  label: string;
  etapa: number;
  sigla: string;
}

/** Uma turma da escola no ano, para os combos de Ensino e Turma. */
export interface TurmaNotasFaltasModel {
  codigo: number;
  descricaoCurta: string;
  descricao: string;
  codigoEnsino: number;
  ensino: string;
  tipoEnturmacao: number;
  semestre: number;
}

/** O que a tela envia ao relatório. */
export class FiltroNotasFaltasModel {
  /**
   * As turmas a emitir, já RESOLVIDAS. A tela traduz "todas as turmas da escola" para a lista
   * concreta antes de emitir: assim o título e a contagem de folhas são decididos com a mesma
   * lista que o SQL vai varrer.
   */
  codigosTurma: number[] = [];

  ano = 0;
  semestre = 1;

  modelo: number = eModeloNotasFaltas.NotasFrequenciasPendentes;

  etapa: EtapaNotasFaltasModel | null = null;

  /** Só o modelo 1 usa. Desmarcado, a folha sai com a turma inteira (conferência). */
  apenasPendentes = true;

  tipoEnturmacao = 0;

  dataEmissao: string | null = null;
}
